import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import JsCookies from "js-cookie";
import toaster from "toasted-notes";
import { useSelector } from "react-redux";
import { Box, Flex, Text, Center, Spinner, Checkbox } from "@chakra-ui/react";

import TopBand from "../src/components/common/topBand";
import CartItem from "../src/components/checkout/cartItem";
import ButtonComponent from "../src/components/common/button";
import DetailsForm from "../src/components/checkout/detailsForm";
import ErrorToaster from "../src/components/common/error/errorToaster";

import { createCheckoutData } from "../src/utils/functions";
import {
  createOrder,
  verifyRazorpayPayment,
} from "../src/services/paymentGatewayService";
import { getValidatedOrderDetails } from "../src/services/paymentGatewayService";

import { ADDRESS_FORM_SCHEMA, DEFAULT_ADDRESS_DETAILS } from "../src/constants";

const validateFormSchema = (values, isShipping) => {
  if (isShipping) {
    delete ADDRESS_FORM_SCHEMA.fields.email;
    delete ADDRESS_FORM_SCHEMA.fields.phone;
  }

  return ADDRESS_FORM_SCHEMA.validate(values, {
    abortEarly: false,
  });
};

export default function Checkout() {
  const router = useRouter();
  const { cart } = useSelector((state) => state);

  const [loading, setLoading] = useState(true);
  const [validatedOrderDetails, setValidatedOrderDetails] = useState(null);

  const [shippingAddress, setShippingAddress] = useState(
    DEFAULT_ADDRESS_DETAILS,
  );
  const [billingErrors, setBillingErrors] = useState(null);
  const [shippingErrors, setShippingErrors] = useState(null);
  const [shipToDifferentAddress, setShipToDifferentAddress] = useState(false);
  const [billingAddress, setBillingAddress] = useState(DEFAULT_ADDRESS_DETAILS);

  useEffect(() => {
    if (cart.length === 0) router.push("/shop");
    else {
      setLoading(false);
      fetchValidatedOrderDetails();
    }
  }, []);

  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  async function displayRazorpay() {
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js",
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const result = await handleCreateRazorpayOrder();
    if (!result) {
      alert("Server error. Are you online?");
      return;
    }

    const { amount, id: order_id, currency, receipt } = result;

    const options = {
      key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID, // Enter the Key ID generated from the Dashboard
      amount: amount.toString(),
      currency: currency,
      name: "Dr.Vasishth's AyuRemedies",
      image:
        "https://www.drvasishths.in/wp-content/uploads/2020/10/onlylogo-e1603604348459.png",
      order_id: order_id,
      handler: async function (response) {
        const data = {
          wooCommerceOrderId: receipt,
          orderCreationId: order_id,
          razorpayPaymentId: response.razorpay_payment_id,
          razorpayOrderId: response.razorpay_order_id,
          razorpaySignature: response.razorpay_signature,
        };

        const result = await handleVerifyRazorpayPayment(data);
        router.push(`/payment/${result.orderId}`);
      },
      theme: {
        color: "#8F1838",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  }

  const handleVerifyRazorpayPayment = async (data) => {
    let accessCookie = JsCookies.get(process.env.NEXT_PUBLIC_NEXTJS_COOKIE);
    try {
      const response = await verifyRazorpayPayment({
        data,
        token: accessCookie,
      });
      if (response && response.success && response.data) {
        return response.data;
      } else {
        toaster.notify(<ErrorToaster errorMessage={response.message} />, {
          duration: 3000,
          position: "top",
        });
      }
    } catch (err) {
      toaster.notify(<ErrorToaster errorMessage={err.message} />, {
        duration: 3000,
        position: "top",
      });
    }
  };

  const fetchValidatedOrderDetails = async () => {
    setLoading(true);
    let accessCookie = JsCookies.get(process.env.NEXT_PUBLIC_NEXTJS_COOKIE);
    try {
      const response = await getValidatedOrderDetails({
        cart,
        token: accessCookie,
      });
      if (response && response.success && response.data) {
        setValidatedOrderDetails(response.data);
        setLoading(false);
      } else {
        router.push("/login");
        toaster.notify(<ErrorToaster errorMessage={response.message} />, {
          duration: 3000,
          position: "top",
        });
      }
    } catch (err) {
      router.push("/login");
      toaster.notify(<ErrorToaster errorMessage={err.message} />, {
        duration: 3000,
        position: "top",
      });
    }
  };

  const handleBillingAddressChange = (e) => {
    setBillingAddress(
      Object.assign({}, billingAddress, {
        [e.target.name]: e.target.value,
      }),
    );
  };

  const handleShippingAddressChange = (e) => {
    setShippingAddress(
      Object.assign({}, shippingAddress, {
        [e.target.name]: e.target.value,
      }),
    );
  };

  const handleShippingAddressChecked = (e) => {
    setShippingErrors(null);
    setShipToDifferentAddress(e.target.checked);
    setShippingAddress(DEFAULT_ADDRESS_DETAILS);
    return;
  };

  const validateCheckoutForm = async () => {
    let validationFlag = true;
    let validationObj;
    if (shipToDifferentAddress) {
      validationObj = { billingAddress, shippingAddress };
    } else {
      validationObj = { billingAddress };
    }

    Promise.all(
      Object.keys(validationObj).map((key) => {
        const isShipping = key === "shippingAddress";
        return validateFormSchema(validationObj[key], isShipping)
          .then(() => {
            switch (key) {
              case "billingAddress":
                setBillingErrors(null);
                break;
              case "shippingAddress":
                setShippingErrors(null);
                break;
            }
          })
          .catch((err) => {
            if (err.inner) {
              validationFlag = false;
              switch (key) {
                case "billingAddress":
                  setBillingErrors(
                    err.inner.reduce((acc, currentValue) => {
                      acc[currentValue.path] = currentValue.message;
                      return acc;
                    }, {}),
                  );
                  break;
                case "shippingAddress":
                  setShippingErrors(
                    err.inner.reduce((acc, currentValue) => {
                      acc[currentValue.path] = currentValue.message;
                      return acc;
                    }, {}),
                  );
                  break;
              }
            } else {
              toaster.notify(<ErrorToaster errorMessage={err.message} />, {
                duration: 3000,
                position: "top",
              });
            }
          });
      }),
    )
      .then(() => {
        if (validationFlag) {
          displayRazorpay();
        }
      })
      .catch((err) =>
        toaster.notify(<ErrorToaster errorMessage={err.message} />, {
          duration: 3000,
          position: "top",
        }),
      );
  };

  const handleCreateRazorpayOrder = async () => {
    try {
      const orderDetails = createCheckoutData(
        cart,
        billingAddress,
        shippingAddress,
        shipToDifferentAddress,
      );

      let accessCookie = JsCookies.get(process.env.NEXT_PUBLIC_NEXTJS_COOKIE);
      const response = await createOrder({ orderDetails, cart }, accessCookie);
      if (response && response.success && response.data) {
        return response.data;
      } else {
        toaster.notify(<ErrorToaster errorMessage={response.message} />, {
          duration: 3000,
          position: "top",
        });
      }
    } catch (err) {
      toaster.notify(<ErrorToaster errorMessage={err.message} />, {
        duration: 3000,
        position: "top",
      });
    }
  };

  const getSpinner = () => (
    <Center
      mt="30"
      height={{
        base: "250px",
        sm: "300px",
        md: "350px",
        lg: "450px",
        xl: "500px",
      }}
    >
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor="gray.200"
        color="maroon"
        size="xl"
      />
    </Center>
  );

  return (
    <>
      <TopBand title="Checkout" />
      {loading ? (
        getSpinner()
      ) : (
        <Center>
          <Flex
            my="3%"
            justifyContent="space-between"
            flexDirection={{ base: "column", lg: "row" }}
            width={{ base: "90%", "2xl": "1350px" }}
          >
            <Flex width={{ base: "100%", lg: "58%" }} flexDirection="column">
              <Text color="maroon" fontSize="24px" fontWeight="light">
                Billing details
              </Text>
              <DetailsForm
                isBilling={true}
                errors={billingErrors}
                address={billingAddress}
                inputChange={handleBillingAddressChange}
              />
              <Box mt="10" mb="5">
                <Checkbox
                  size="md"
                  value={shipToDifferentAddress}
                  onChange={handleShippingAddressChecked}
                >
                  Ship to a different address?
                </Checkbox>
              </Box>
              {shipToDifferentAddress && (
                <Box>
                  <Text color="maroon" fontSize="24px" fontWeight="light">
                    Shipping details
                  </Text>
                  <DetailsForm
                    errors={shippingErrors}
                    address={shippingAddress}
                    inputChange={handleShippingAddressChange}
                  />
                </Box>
              )}
            </Flex>

            <Flex
              flexDirection="column"
              mb={{ base: "10", lg: "0" }}
              mt={{ base: "10", lg: "0" }}
              width={{ base: "100%", lg: "38%" }}
            >
              <Text color="maroon" fontSize="24px" fontWeight="light">
                Your order
              </Text>

              <Flex
                pt="5"
                pb="2"
                px="5"
                width="100%"
                borderBottomWidth={1}
                justifyContent="space-between"
              >
                <Text width="50%" fontWeight="500">
                  Product
                </Text>
                <Text fontWeight="500">Subtotal</Text>
              </Flex>

              {validatedOrderDetails &&
                validatedOrderDetails.cart &&
                validatedOrderDetails.cart.map((product) => (
                  <CartItem key={product.id} product={product} />
                ))}

              <Flex
                mt="2"
                pt="3"
                pb="2"
                width="100%"
                borderTopWidth={1}
                px="5"
                justifyContent="space-between"
              >
                <Text fontWeight="500">Subtotal</Text>
                <Text color="detailsColor" fontWeight="600">
                  ₹{validatedOrderDetails.subTotal.toFixed(2)}
                </Text>
              </Flex>
              <Flex
                pt="2"
                pb="3"
                width="100%"
                px="5"
                justifyContent="space-between"
              >
                <Text fontWeight="500">
                  {/* Shipping (Free for Orders ₹1000 or above) */}
                  Please expect delay in order delivery times due to heavy
                  rainfall
                </Text>
                <Text color="detailsColor" fontWeight="600">
                  ₹{validatedOrderDetails.shippingCost.toFixed(2)}
                </Text>
              </Flex>

              <Flex
                mt="2"
                pt="2"
                pb="2"
                width="100%"
                borderTopWidth={1}
                px="5"
                justifyContent="space-between"
              >
                <Text fontWeight="500">Total</Text>
                <Text color="maroon" fontWeight="600">
                  ₹{validatedOrderDetails.total.toFixed(2)}
                </Text>
              </Flex>

              <ButtonComponent
                mt="4"
                borderRadius={2}
                title="Place order"
                backgroundColor="maroon"
                handleClick={validateCheckoutForm}
              />
            </Flex>
          </Flex>
        </Center>
      )}
    </>
  );
}
